import {
    ControlReadiness,
    ControlState,
    PlatformControl,
    RestingState,
} from '../types';

/**
 * FNP-891 / FNP-893 — the platform's operational controls.
 *
 * Every control is listed, INCLUDING the ones that are not wired up yet. That is
 * deliberate and is the reason this module exists: a control nobody can see is a
 * control nobody remembers, and unseen work decays into forgotten backlog.
 * Unwired rows render disabled, name why, and link the ticket that closes the gap.
 *
 * Verified 2026-08-11: there is NO runtime SSM/AppConfig read in any Lambda on
 * this platform — every SSM use today is CDK synth/deploy-time. Registration
 * (FNP-891) is the first control to become runtime-flippable; the other three
 * are deploy-gated, which is exactly what their owning tickets exist to change.
 *
 * Adding a control here is display-only. Wiring one means giving it a real read
 * (and, if it is to be operable, a write) — see the registration entry.
 */
export const platformControls: PlatformControl[] = [
    {
        id: 'user-registration',
        name: 'User registration',
        description: 'Allow new users to create an account. Sign-in, sign-out and existing sessions are unaffected.',
        enforcedAt: 'Cognito PreSignUp trigger (main user pool)',
        restingState: RestingState.ON,
        readiness: ControlReadiness.OPERATIONAL,
        // Resolved at runtime by the query hook; UNKNOWN until the read lands.
        state: ControlState.UNKNOWN,
        flipLatencySeconds: 60,
    },
    {
        id: 'ingress-allowlist',
        name: 'Admin ingress IP allowlist',
        description: 'Which source IPs may reach the admin consoles through the CloudFront WAF.',
        enforcedAt: 'WAFv2 IPSet (shared-svcs / billing / marketplace)',
        restingState: RestingState.ON,
        readiness: ControlReadiness.NOT_WIRED,
        state: ControlState.UNKNOWN,
        notWiredReason: 'The allowlist is still a hardcoded literal in CDK, so changing it needs three repos, three pipelines and three prod approvals. FNP-792 moves the IPSet contents out of CloudFormation so it becomes data; FNP-793 adds the allow-my-ip CLI.',
        owningTicket: 'FNP-792',
    },
    {
        id: 'cedar-agent-authorization',
        name: 'Agent authorization enforcement',
        description: 'Whether Cedar policy decisions actually block agent-gateway calls, or are only logged.',
        enforcedAt: 'Bedrock AgentCore Policy Engine (billing agent gateway)',
        restingState: RestingState.OFF,
        readiness: ControlReadiness.NOT_WIRED,
        state: ControlState.UNKNOWN,
        notWiredReason: 'Runs in LOG_ONLY everywhere by decision, so a Cedar DENY is logged and the call proceeds — the emergency-shutdown policy currently cannot block anything. Mode is a CloudFormation property with no runtime read, so flipping it needs a deploy.',
        owningTicket: 'FNP-898',
    },
    {
        id: 'stripe-meter-provisioner-dry-run',
        name: 'Stripe meter provisioner dry-run',
        description: 'Whether the Stripe meter reconciler only reports what it would do, instead of writing.',
        enforcedAt: 'flexinet-billing-stripe-integration (meter reconciler)',
        restingState: RestingState.ON,
        readiness: ControlReadiness.NOT_WIRED,
        state: ControlState.UNKNOWN,
        notWiredReason: 'Read from a Lambda environment variable set at deploy time, so switching between dry-run and live requires a deploy rather than a flip. Money-path — any change here needs explicit sign-off.',
    },
];
